import type { FilmFrame } from '@/components/landing/film/frame'

export type OverlayUpdater = (t: number, real: number, frame: FilmFrame) => void

export type RegisterUpdater = (updater: OverlayUpdater) => () => void

type StyledElement = HTMLElement | SVGElement

const styleCache = new WeakMap<StyledElement, Record<string, string>>()

// Writes a style property only when it actually changes, so the 60fps loop stays cheap
export const setStyle = (element: StyledElement | null | undefined, property: string, value: string): void => {
    if (!element) return
    let entry = styleCache.get(element)
    if (!entry) {
        entry = {}
        styleCache.set(element, entry)
    }
    if (entry[property] === value) return
    entry[property] = value
    element.style.setProperty(property, value)
}

const attrCache = new WeakMap<Element, Record<string, string>>()

export const setAttr = (element: Element | null | undefined, name: string, value: string): void => {
    if (!element) return
    let entry = attrCache.get(element)
    if (!entry) {
        entry = {}
        attrCache.set(element, entry)
    }
    if (entry[name] === value) return
    entry[name] = value
    element.setAttribute(name, value)
}

const textCache = new WeakMap<Node, string>()

export const setText = (element: HTMLElement | SVGElement | null | undefined, value: string): void => {
    if (!element) return
    if (textCache.get(element) === value) return
    textCache.set(element, value)
    element.textContent = value
}

// Shows or hides a whole layer, toggling interactivity with it
export const setLayer = (element: HTMLElement | null | undefined, opacity: number): void => {
    if (!element) return
    setStyle(element, 'opacity', opacity.toFixed(3))
    setStyle(element, 'visibility', opacity > 0.001 ? 'visible' : 'hidden')
}

export const fixed = (value: number, digits = 2): string => value.toFixed(digits)

// Deterministic pseudo random in [0, 1) for a given index and salt
export const hash = (index: number, salt = 0): number => {
    const x = Math.sin(index * 127.1 + salt * 311.7) * 43758.5453
    return x - Math.floor(x)
}

// Approximate anchors used before the 3D engine is ready, or when the device cannot run it
export const fillFallbackFrame = (frame: FilmFrame, width: number, height: number): FilmFrame => {
    const portrait = width / Math.max(1, height) < 1
    const cx = width / 2
    const cy = height * (portrait ? 0.42 : 0.46)
    const r = Math.min(width, height) * (portrait ? 0.34 : 0.3)

    frame.width = width
    frame.height = height
    frame.portrait = portrait
    frame.wheel.x = cx
    frame.wheel.y = cy
    frame.wheel.r = r
    frame.wheel.visible = true
    frame.spoke.x = cx + r * 0.25
    frame.spoke.y = cy - r * 0.42
    frame.spoke.visible = true
    frame.hub.x = cx + r * 0.12
    frame.hub.y = cy + r * 0.08
    frame.hub.visible = true
    frame.lip.x = cx - r * 0.72
    frame.lip.y = cy + r * 0.6
    frame.lip.visible = true
    frame.cap.x = cx
    frame.cap.y = cy
    frame.cap.visible = true
    frame.triptych.forEach((item, i) => {
        item.x = portrait ? cx : width * (0.2 + i * 0.3)
        item.y = portrait ? height * (0.2 + i * 0.3) : cy
        item.r = portrait ? height * 0.11 : width * 0.11
        item.visible = true
    })
    return frame
}
